/**
 * 実数入力向けの radix-2 FFT。
 *
 * オフライン解析には AnalyserNode が使えない（再生していないので）ため、
 * 振幅スペクトルを自前で求める必要がある。用途は特徴量抽出だけなので、
 * 素直な反復 Cooley-Tukey で十分速い（2048 点で 1 フレーム数十マイクロ秒）。
 */

/** 長さ n の Hann 窓。フレーム端の不連続によるスペクトル漏れを抑える */
export function hannWindow(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return w;
}

/**
 * FFT の作業領域。回転因子とビット反転表を使い回すため、
 * 同じサイズで繰り返し変換するときは 1 つ作って再利用すること。
 */
export interface FftPlan {
  readonly size: number;
  /** 出力される振幅スペクトルの長さ（size / 2） */
  readonly binCount: number;
  /**
   * frame の振幅スペクトルを magnitude に書き込む。
   * frame は size 長、magnitude は binCount 長であること。
   */
  forward(_frame: Float32Array, _magnitude: Float32Array): void;
}

export function createFftPlan(size: number): FftPlan {
  if (size < 2 || (size & (size - 1)) !== 0) {
    throw new Error(`FFT size must be a power of two: ${size}`);
  }

  const binCount = size >> 1;
  const reverse = new Uint32Array(size);
  const bits = Math.log2(size);
  for (let i = 0; i < size; i++) {
    let r = 0;
    for (let b = 0; b < bits; b++) {
      r = (r << 1) | ((i >> b) & 1);
    }
    reverse[i] = r;
  }

  // 各段の回転因子を先に展開しておく（段ごとに cos/sin を呼ばない）
  const cos = new Float32Array(binCount);
  const sin = new Float32Array(binCount);
  for (let i = 0; i < binCount; i++) {
    cos[i] = Math.cos((-2 * Math.PI * i) / size);
    sin[i] = Math.sin((-2 * Math.PI * i) / size);
  }

  const re = new Float32Array(size);
  const im = new Float32Array(size);

  return {
    size,
    binCount,
    forward(frame: Float32Array, magnitude: Float32Array): void {
      for (let i = 0; i < size; i++) {
        re[i] = frame[reverse[i]];
        im[i] = 0;
      }

      for (let half = 1; half < size; half <<= 1) {
        const step = size / (half << 1);
        for (let start = 0; start < size; start += half << 1) {
          for (let k = 0; k < half; k++) {
            const twiddle = k * step;
            const c = cos[twiddle];
            const s = sin[twiddle];
            const a = start + k;
            const b = a + half;
            const tr = re[b] * c - im[b] * s;
            const ti = re[b] * s + im[b] * c;
            re[b] = re[a] - tr;
            im[b] = im[a] - ti;
            re[a] += tr;
            im[a] += ti;
          }
        }
      }

      // AnalyserNode の getFloatFrequencyData と同じ尺度になるよう size で割る
      for (let i = 0; i < binCount; i++) {
        magnitude[i] = Math.sqrt(re[i] * re[i] + im[i] * im[i]) / size;
      }
    },
  };
}
